import { useState, useCallback, useMemo, useRef } from "react";
import _ from "lodash";

import { getNumberValue } from "../../../../utils";

const getItemKey = (item) => {
  if (item.action === "create") return item._id;
  return _.get(item, "data.payment_id") || _.get(item, "data.mp_number");
};

/**
 * Hook that provides MP/PI expand/collapse for DashboardTable.
 *
 * When `portfolio.isModelportfolio` is true, interleaves read-only asset component
 * rows after each transaction item. Each asset row carries `allocated_amount`
 * derived from its parent item's transaction_value * weight.
 *
 * Expand/collapse state is tracked by item identity (same as isSameAsset pattern).
 *
 * @param {object} portfolio - portfolio object with `isModelportfolio` and `data`
 * @param {Array} items - the actual transaction items (passed through unchanged in state)
 * @returns {{ dataSource: Array, expandSummaryLineChange: function, checkExpanded: function }}
 */
export const useMPSummaryLine = (portfolio, items) => {
  const { isModelportfolio } = portfolio;
  const components = _.get(portfolio, 'data.components');

  const isPrivateInvestment = _.get(portfolio, "data.is_private_investment");
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const knownKeysRef = useRef(new Set());
  // React-sanctioned pattern: adjust state during render based on changed props.
  // See https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (isPrivateInvestment) {
    const newKeys = [];
    items.forEach((item) => {
      const key = getItemKey(item);
      if (key != null && !knownKeysRef.current.has(key)) {
        knownKeysRef.current.add(key);
        newKeys.push(key);
      }
    });
    if (newKeys.length > 0) {
      setCollapsedKeys((prev) => {
        const next = new Set(prev);
        newKeys.forEach((k) => next.add(k));
        return next;
      });
    }
  }

  const expandSummaryLineChange = useCallback((item) => {
    const key = getItemKey(item);
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const checkExpanded = useCallback((item) => {
    return !collapsedKeys.has(getItemKey(item));
  }, [collapsedKeys]);

  const dataSource = useMemo(() => {
    if (!isModelportfolio || !items.length) {
      return items;
    }

    const filteredComponents = (components || []).filter((component) => !component.is_profit_loss);

    if (!filteredComponents.length) return items;

    const result = [];
    items.forEach((item) => {
      // Push original item reference as-is. Spreading would create a new object
      // on every recompute, causing React to re-render inputs and lose focus/state.
      result.push(item);

      if (!checkExpanded(item)) return;

      // Asset rows are synthetic read-only objects (no inputs), safe to recreate each time.
      const itemAmount = getNumberValue(item.transaction_value) || 0;
      filteredComponents.forEach((component) => {
        const weight = parseFloat(component.weight) || 0;
        result.push({
          data: component,
          weight: weight,
          allocated_amount: itemAmount * (weight / 100),
          mpComponent: true,
        });
      });
    });

    return result;
  }, [isModelportfolio, components, items, checkExpanded]);

  return { dataSource, expandSummaryLineChange, checkExpanded };
};
